import React, { useEffect } from "react"
import styles from "./ArticleCommentThreadView.module.scss"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { selectCurrentProjectId } from "../../store/projects/selectors"
import Async from "../Async/Async"
import {
    selectArticleCommentInputStates,
    selectArticleCommentThreadState,
    selectArticleReplySendState
} from "../../store/knowledgeBaseComments/selectors"
import {
    cancelArticleCommentInput,
    enableArticleCommentInputMode,
    getArticleCommentThread
} from "../../store/knowledgeBaseComments/thunks"
import ArticleCommentThreadViewLoader from "./ArticleCommentThreadViewLoader"
import ErrorMessage from "../ErrorMessage/ErrorMessage"
import { ArticleComment, ArticleCommentInput, ArticleCommentThread } from "../../models/articleComment"
import ArticleCommentComponent from "../ArticleComment/ArticleCommentComponent"
import { selectCurrentUser } from "../../store/users/selectors"
import MessageInput from "../MessageInput/MessageInput"
import { Button } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { getThreadInputCode } from "../../utility/knowledgeBase/articleComment"

const tNamespace = "knowledgeBase:comment."

interface Props {
    rootComment: ArticleComment
    articleTitle: string
    onReplySend: (articleTitle: string, rootArticleComment: ArticleComment, value: ArticleCommentInput) => void
}

const ArticleCommentThreadView: React.FC<Props> = props => {
    const { rootComment, onReplySend, articleTitle } = props

    const { t } = useTranslation()
    const threadInputCode = getThreadInputCode(rootComment.ArticleCode, rootComment.Id)
    const dispatch = useAppDispatch()
    const projectId = useAppSelector(selectCurrentProjectId)
    const user = useAppSelector(selectCurrentUser)
    const articleCommentThreadState = useAppSelector(selectArticleCommentThreadState)
    const articleReplySendState = useAppSelector(selectArticleReplySendState)
    const articleCommentInputStates = useAppSelector(selectArticleCommentInputStates)

    useEffect(() => {
        if (projectId) {
            dispatch(getArticleCommentThread(projectId, rootComment))
        }
    }, [dispatch, projectId, rootComment])

    const inputState = articleCommentInputStates[threadInputCode]
    const handleSend = (message: string) => onReplySend(articleTitle, rootComment, { MessageText: message })
    const handleCancel = () => cancelArticleCommentInput(dispatch, threadInputCode)
    const handleEnableInput = () => enableArticleCommentInputMode(dispatch, threadInputCode)

    return (
        <Async<ArticleCommentThread>
            dataState={articleCommentThreadState}
            processView={<ArticleCommentThreadViewLoader />}
            errorView={({ message }) => <ErrorMessage text={message} />}
        >
            {({ data: replies }) => (
                <div className={styles.articleCommentThreadView}>
                    <div className={styles.articleCommentThreadView__replies}>
                        {[rootComment, ...replies.Items].map(c => (
                            <ArticleCommentComponent key={c.Id} data={c} />
                        ))}
                    </div>
                    <div className={styles.articleCommentThreadView__inputContainer}>
                        {inputState ? (
                            <MessageInput
                                onSend={handleSend}
                                onCancel={handleCancel}
                                user={user}
                                loading={articleReplySendState.inProcess}
                            />
                        ) : (
                            <Button
                                className={styles.articleCommentThreadView__replyButton}
                                variant="link"
                                onClick={handleEnableInput}
                            >
                                {t(`${tNamespace}to-reply`)}
                            </Button>
                        )}
                    </div>
                </div>
            )}
        </Async>
    )
}

export default ArticleCommentThreadView
